// Messages au support (D-082) : libellés et règles partagés par l'écran « Écrire au support »,
// « Mes messages » et la boîte du support. Les limites sont aussi celles de la base
// (migration 20261027330000_support.sql) : l'écran prévient, la base décide.

export type SupportKind = 'bug' | 'idea' | 'question';
export type SupportStatus = 'new' | 'seen' | 'in_progress' | 'test' | 'done' | 'wontfix';

export const BODY_MAX = 4000;
/** Capture d'écran : data URL de 700 Ko au plus (contrôlé par la base). */
export const SHOT_MAX = 716800;
/** Messages par compte et par 24 h (contrôlé par la base). */
export const DAILY_MAX = 10;

export const KIND_LABEL: Record<SupportKind, string> = { bug: 'Bug', idea: 'Idée', question: 'Question' };
/** Sans tutoiement ni vouvoiement : l'espace partenaire vouvoie (D-074). */
export const KIND_HINT: Record<SupportKind, string> = {
  bug: 'Ce qui ne marche pas : ce qu’on voulait faire, ce qu’on a touché, ce qui s’est passé.',
  idea: 'Une amélioration, une fonction qui manque, et à quoi elle servirait.',
  question: 'Comment faire quelque chose dans l’app : la réponse arrive dans « Mes messages ».',
};
/** Pour l'auteur : « Envoyé » plutôt que « Nouveau ». */
export const STATUS_LABEL: Record<SupportStatus, string> = {
  new: 'Envoyé', seen: 'Lu', in_progress: 'En cours', test: 'En test', done: 'Traité', wontfix: 'Pas retenu',
};
export const STATUS_ADMIN: Record<SupportStatus, string> = { ...STATUS_LABEL, new: 'Nouveau' };
export const STATUS_CHIP: Record<SupportStatus, string> = {
  new: 'chip-accent', seen: 'chip-soft', in_progress: 'chip-warn', test: 'chip-warn', done: 'chip-ok', wontfix: 'chip-mute',
};
export const STATUSES: SupportStatus[] = ['new', 'seen', 'in_progress', 'test', 'done', 'wontfix'];
/** Encore à traiter par l'équipe Arceau. */
export const isOpen = (s: SupportStatus) => s === 'new' || s === 'seen' || s === 'in_progress';

/** « Safari · iPhone », « Chrome · Android »… : ce que voit l'auteur avant l'envoi (le texte complet part aussi). */
export function browserLabel(ua: string): string {
  const os = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows' : /Mac OS X|Macintosh/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  const v = (re: RegExp) => re.exec(ua)?.[1]?.split('.')[0] ?? '';
  const nav = /Edg(?:A|iOS)?\/([\d.]+)/.test(ua) ? `Edge ${v(/Edg(?:A|iOS)?\/([\d.]+)/)}`
    : /SamsungBrowser\/([\d.]+)/.test(ua) ? `Samsung Internet ${v(/SamsungBrowser\/([\d.]+)/)}`
    : /Firefox\/([\d.]+)|FxiOS\/([\d.]+)/.test(ua) ? `Firefox ${v(/(?:Firefox|FxiOS)\/([\d.]+)/)}`
    : /CriOS\/([\d.]+)|Chrome\/([\d.]+)/.test(ua) ? `Chrome ${v(/(?:CriOS|Chrome)\/([\d.]+)/)}`
    : /Version\/([\d.]+).*Safari/.test(ua) ? `Safari ${v(/Version\/([\d.]+)/)}`
    : /Safari/.test(ua) ? 'Safari' : 'Navigateur';
  return [nav.trim(), os].filter(Boolean).join(' · ');
}

/** « Page » jointe au message, en mots : les mêmes noms que les écrans (D-101 : un mot, un sens) ; chaque route de
 *  l'app a le sien (vérifié par src/guide/guide.test.ts). */
export const PAGE_NAMES: Record<string, string> = {
  tournoi: 'Le tournoi du club', boutique: 'La boutique du club',   // D-181
  buvette: 'La buvette',   // D-184
  maillots: 'Qui lave les maillots ?',   // D-188
  '': 'Accueil', 'a-faire': 'À faire (accueil)', e: 'Fiche d’une séance ou d’un match', t: 'Page d’une équipe', planning: 'Planning', basket: 'Basket', plays: 'Plays',
  play: 'Fiche d’un play', seance: 'Plan de séance', matchs: 'Matchs', 'matchs-club': 'Matchs du club', prepa: 'Prépa physique',
  stats: 'Stats en direct', table: 'Table de marque', club: 'Club', notifs: 'Notifications', annonces: 'Annonces', benevoles: 'Bénévolat', licences: 'Licences',
  essais: 'Essais et demandes', membres: 'Membres et rôles', inviter: 'Inviter', partenaires: 'Partenaires', agenda: 'Agenda', moi: 'Moi',
  enfants: 'Mes enfants', indispos: 'Indisponibilités', avantages: 'Avantages', marrainage: 'Parrainage des jeunes', defis: 'Défis et gages',
  derogations: 'Dérogations', feuilles: 'Feuilles de match', visibilite: 'Visibilité', publier: 'Publier', support: 'Boîte du support',
  pistes: 'Trouver un partenaire', prospection: 'Prospection', badges: 'Mes badges', repartir: 'Deux matchs le même jour', evaluation: 'Évaluation', composition: 'Composition',
  rt: 'Espace du responsable technique', aide: 'Aide', console: 'Console', suivis: 'Mes suivis', subventions: 'Subventions', salles: 'Les salles',
  lien: 'Garder le lien', formation: 'Formation des officiels', 'mains-vives': 'Mains vives (jeu caméra)',   // D-164
  ressentis: 'Ressentis', bilan: 'Bilan de saison', 'nouvelle-saison': 'Nouvelle saison',   // D-143
  accords: 'Les accords (photos, transport)', dossier: 'Dossier de licence', 'photos-a-revoir': 'Photos à revoir',   // D-169
  tresorerie: 'Trésorerie',
};
export const pageName = (hash: string) => PAGE_NAMES[hash.replace(/^#\/?/, '').split('/')[0] ?? ''] ?? 'Autre page';

/** La page d'où l'on écrit, lisible : « #/e/…/2026-10-28 » garde son chemin, sans le domaine. */
export function pageOf(hash: string): string {
  const h = hash.startsWith('#') ? hash : '';
  return h.length > 200 ? h.slice(0, 200) : h;
}

// --- « 💬 Ton avis » (D-103) : les retours du club pilote -------------------------------------------
// Un avis est un message au support avec un sujet (migration 20261030500000_ton_avis.sql) : la sorte en
// découle (posée par la base), l'auteur le suit dans « Mes messages », l'équipe Arceau le lit dans la boîte.

export type AvisTopic = 'bug' | 'confusion' | 'missing' | 'idea';
export type AvisOthers = 'yes' | 'no' | 'unknown';
export const AVIS_TOPICS: AvisTopic[] = ['bug', 'confusion', 'missing', 'idea'];
export const TOPIC_LABEL: Record<AvisTopic, string> = {
  bug: 'Ça ne marche pas', confusion: 'Je n’ai pas compris', missing: 'Il manque quelque chose', idea: 'Une idée',
};
/** Pour la boîte du support (pastilles courtes). */
export const TOPIC_SHORT: Record<AvisTopic, string> = { bug: 'Ne marche pas', confusion: 'Pas compris', missing: 'Il manque', idea: 'Idée' };
/** La sorte du message qui découle du sujet (la base la pose de même). */
export const TOPIC_KIND: Record<AvisTopic, SupportKind> = { bug: 'bug', confusion: 'question', missing: 'idea', idea: 'idea' };
export const OTHERS_LABEL: Record<AvisOthers, string> = { yes: 'Oui', no: 'Non', unknown: 'Je ne sais pas' };
export const OTHERS: AvisOthers[] = ['yes', 'no', 'unknown'];
/** Nom de page joint à un avis (contrôlé par la base). */
export const PAGE_LABEL_MAX = 160;

/** Ce qu'on attend dans le message, selon le sujet (tutoiement ; vouvoiement de l'espace partenaire, D-074). */
export function topicHint(t: AvisTopic | null, vous = false): string {
  switch (t) {
    case 'bug': return vous ? 'Ce que vous vouliez faire, ce que vous avez touché, ce qui s’est passé.' : 'Ce que tu voulais faire, ce que tu as touché, ce qui s’est passé.';
    case 'confusion': return vous ? 'Le mot, le bouton ou l’étape qui pose question, et ce que vous pensiez trouver.' : 'Le mot, le bouton ou l’étape qui pose question, et ce que tu pensais trouver.';
    case 'missing': return vous ? 'Ce que vous cherchiez sur cet écran, et à quoi cela vous servirait.' : 'Ce que tu cherchais sur cet écran, et à quoi ça te servirait.';
    case 'idea': return vous ? 'Votre idée, et ce qu’elle changerait pour le club.' : 'Ton idée, et ce qu’elle changerait pour le club.';
    default: return vous ? 'Choisissez d’abord ce qui se passe.' : 'Choisis d’abord ce qui se passe.';
  }
}

/** « Planning · Seniors filles » : la page en mots, telle que l'auteur l'a vue. `screen` précise un écran de
 *  l'espace du responsable technique (« Espace du responsable technique › Projet sportif »). La tâche SAV
 *  regroupe les avis par la partie avant « · » (sans l'équipe). */
export function avisPageLabel(hash: string, team?: string | null, screen?: string | null): string {
  const base = pageName(hash);
  const head = screen && screen !== base ? `${base} › ${screen}` : base;
  return [head, team?.trim()].filter(Boolean).join(' · ').slice(0, PAGE_LABEL_MAX);
}

/** Le libellé d'un message dans « Mes messages » et la boîte : le sujet d'un avis, sinon la sorte. */
export function messageLabel(m: { kind: SupportKind; topic?: AvisTopic | null }, vous = false): string {
  return m.topic ? `${vous ? 'Votre' : 'Ton'} avis · ${TOPIC_LABEL[m.topic]}` : KIND_LABEL[m.kind];
}

/** Version de l'app : celle que le serveur publie avec chaque mise en ligne (version.txt). */
export function cleanVersion(text: string | null | undefined): string | null {
  const t = (text ?? '').trim();
  return /^[\w.+-]{1,64}$/.test(t) ? t : null;
}
